import { Menu, ActionIcon, rem, CopyButton, Anchor } from "@mantine/core"
import {
  IconCopy,
  IconDotsVertical,
  IconDownload,
  IconEdit,
  IconTrash,
} from "@tabler/icons-react"
import { WebCalendarProfileDto } from "../../../../../@client/Theasoft_Shared_Contracts_WebCalendar"
import { GetProfilesResponse } from "../../../../../@client/Theasoft_ApiServer_WebCalendar"
import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { usePostMutation } from "../../../../../typed-client/hooks/use-post-mutation"
import { useIsSmallScreen } from "../../../../../utils/useIsSmallScreen"

interface Props {
  profile: WebCalendarProfileDto
  onEditProfileClick: (profile: WebCalendarProfileDto) => void
  isPrivate?: boolean
}

export function CopyCalendarProfileLinkButton({
  profile,
  component: component,
}: {
  profile: WebCalendarProfileDto
  component: "Button" | "MenuItem"
}) {
  return (
    <CopyButton value={profile.externalLink}>
      {({ copied, copy }) =>
        component == "MenuItem" ? (
          <Menu.Item
            onClick={copy}
            leftSection={<IconCopy size={14} />}
            color={copied ? "teal" : "blue"}
            variant="subtle"
          >
            <ProfileLinkAnchor
              copied={copied}
              copy={copy}
              link={profile.externalLink}
            />
          </Menu.Item>
        ) : (
          <ProfileLinkAnchor
            copied={copied}
            copy={copy}
            link={profile.externalLink}
          />
        )
      }
    </CopyButton>
  )
}

function ProfileLinkAnchor({
  copied,
  link,
  copy,
}: {
  copied: boolean
  link: string
  copy: () => void
}) {
  const { t } = useTranslation("app_server")
  return (
    <Anchor
      c={copied ? "teal" : "blue"}
      size="xs"
      onClick={(e) => {
        e.preventDefault()
        copy()
      }}
      href={link}
    >
      {copied ? t("CopiedLink") : t("CopyLink")}
    </Anchor>
  )
}

export function MoreMenu({ profile, onEditProfileClick, isPrivate }: Props) {
  const { t } = useTranslation("app_server")
  const client = useQueryClient()
  const isMobile = useIsSmallScreen()
  const deleteProfile = usePostMutation(
    "/api/web-calendar/delete-personal-web-calendar-profile",
    {
      onSuccess: (_response, variables) => {
        const id = variables
        client.setQueryData(
          ["/api/web-calendar/get-profiles", null],
          (oldData: GetProfilesResponse): GetProfilesResponse => ({
            ...oldData,
            personalProfiles: oldData.personalProfiles.map((x) => ({
              ...x,
              deleted: x.id === id ? true : undefined,
            })),
          })
        )

        client.invalidateQueries({
          queryKey: ["/api/web-calendar/get-profiles", null],
        })
      },
    }
  )

  return (
    <Menu withinPortal position="bottom-end" shadow="sm">
      <Menu.Target>
        <ActionIcon variant="subtle" color="gray">
          <IconDotsVertical style={{ width: rem(16), height: rem(16) }} />
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown>
        {isMobile ? (
          <CopyCalendarProfileLinkButton
            profile={profile}
            component="MenuItem"
          />
        ) : null}

        <Menu.Item
          onClick={() => {
            window.location.href = profile.internalLink
          }}
          leftSection={
            <IconDownload
              style={{
                width: rem(14),
                height: rem(14),
              }}
            />
          }
        >
          {t("Download")}
        </Menu.Item>
        {isPrivate && (
          <>
            <Menu.Item
              leftSection={
                <IconEdit
                  style={{
                    width: rem(14),
                    height: rem(14),
                  }}
                />
              }
              onClick={() => onEditProfileClick(profile)}
            >
              {t("Edit")}
            </Menu.Item>
            <Menu.Item
              onClick={() => deleteProfile.mutate(profile.id)}
              leftSection={
                <IconTrash
                  style={{
                    width: rem(14),
                    height: rem(14),
                  }}
                />
              }
              color="red"
            >
              {t("Delete")}
            </Menu.Item>
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  )
}
